/**
 * Onde o `resposta_token` fica enquanto o convidado responde.
 *
 * Três escolhas, cada uma pelo mesmo motivo — o token É o acesso àquela
 * resposta:
 *
 *  - **`localStorage`, e ele SOBREVIVE ao fechamento da aba.** Esta linha já foi
 *    o contrário, e a troca é decisão de produto tomada com o custo na mão.
 *    Ver a seção abaixo.
 *  - **Nunca cookie.** Cookie viaja em toda requisição ao domínio, inclusive
 *    para quem hospeda estático, e o segredo passa a existir em log de servidor
 *    que ninguém está guardando de propósito.
 *  - **Por token público**, não uma chave só. Quem organiza abre o próprio link
 *    de dois formulários na mesma aba; uma chave só faria o segundo sobrescrever
 *    o primeiro, e a resposta do primeiro ficaria órfã no meio do caminho.
 *
 * ── Por que deixou de ser `sessionStorage` ──────────────────────────────────
 *
 * `sessionStorage` morre com a aba, e isso resolvia o dispositivo compartilhado
 * de graça: o próximo a abrir o navegador não herdava a resposta de quem
 * confirmou antes. O preço era o outro lado — quem fechava a aba e voltava pelo
 * mesmo link começava um formulário NOVO, e o formulário recebia a mesma pessoa
 * duas vezes sem ninguém perceber. Num link aberto (um endereço para todos), o
 * banco não tem como reconhecer quem voltou: ele não sabe quem é.
 *
 * A decisão foi trocar a proteção automática por uma EXPLÍCITA. Quem volta vê
 * que a resposta já está registrada, com o e-mail dela mascarado, e um botão de
 * "não sou eu" que descarta o token e abre um formulário limpo.
 *
 * Isso é melhor que o silêncio dos dois lados: quem voltou por engano para de
 * duplicar, e quem pegou um celular emprestado tem uma saída visível em vez de
 * um formulário que se recusa a começar. O botão não é enfeite — ele é a metade
 * que torna esta escolha defensável, e apagá-lo reabre o problema do dispositivo
 * compartilhado sem nada no lugar.
 *
 * O que fica guardado continua sendo SÓ o token da resposta. O e-mail que a tela
 * mostra mascarado vem do banco, por `retomar`, e nunca é escrito aqui — dado
 * pessoal em armazenamento de navegador é o tipo de coisa que sobrevive ao
 * evento, ao formulário e à conversa que o justificou.
 *
 * Toda leitura e escrita é embrulhada: navegador em janela privada, ou com
 * armazenamento bloqueado, LANÇA no acesso — e um convite que não abre porque o
 * visitante navega anônimo é pior do que um que só não retoma de onde parou.
 */

/** O que este módulo precisa de um armazenamento. Injetável para teste. */
export type Armazem = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const PREFIXO = 'rsvp.convite.';

function padrao(): Armazem | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function chave(tokenPublico: string): string {
  return PREFIXO + tokenPublico;
}

export function lembrarRespostaToken(
  tokenPublico: string,
  respostaToken: string,
  armazem: Armazem | null = padrao(),
): void {
  try {
    armazem?.setItem(chave(tokenPublico), respostaToken);
  } catch {
    // Sem retomada. O convidado ainda responde — só não sobrevive a um F5, e o
    // aviso de "já respondido" não aparece para ele. Navegador em janela privada
    // ou com armazenamento bloqueado LANÇA no acesso, e um convite que não abre
    // porque o visitante navega anônimo é pior do que um que só não lembra.
  }
}

export function respostaTokenLembrado(
  tokenPublico: string,
  armazem: Armazem | null = padrao(),
): string | null {
  try {
    const guardado = armazem?.getItem(chave(tokenPublico));
    return guardado && guardado !== '' ? guardado : null;
  } catch {
    return null;
  }
}

/**
 * Esquece.
 *
 * **Não é para chamar ao concluir**, e a primeira versão deste comentário dizia
 * que era. Passado o fim, o token continua servindo: é ele que faz um F5 na tela
 * final trazer de volta a mensagem e as instruções de chegada, em vez de abrir um
 * formulário novo. Apagá-lo ali seria criar o defeito que `retomar` conserta.
 *
 * O lugar certo é quando o BANCO diz que o token não abre nada — a recusa
 * `sessao_invalida` de `retomar`. Aí ele é segredo sem uso, e segredo sem uso é
 * só superfície.
 */
export function esquecerRespostaToken(
  tokenPublico: string,
  armazem: Armazem | null = padrao(),
): void {
  try {
    armazem?.removeItem(chave(tokenPublico));
  } catch {
    // Nada a fazer: já não havia onde guardar.
  }
}
